import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import NewsCard from "../components/home/NewsCard";
import { Reveal } from "../components/Reveal";
import api from "../lib/api";
import { TEAMS } from "../lib/site";
import { dayMonth, fmtDate } from "../lib/format";

function Block({ title, children, testId }) {
  return (
    <section className="py-16 sm:py-20 border-b border-navy/10 last:border-0" data-testid={testId}>
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-10 bg-rouge" />
            <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-4xl">{title}</h2>
          </div>
        </Reveal>
        {children}
      </div>
    </section>
  );
}

export default function TeamDetail() {
  const { slug } = useParams();
  const team = TEAMS[slug];
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    if (!team) return;
    api.get(`/events?team=${slug}`).then((r) => setEvents(r.data)).catch(() => {});
    api.get(`/results?team=${slug}`).then((r) => setResults(r.data)).catch(() => {});
    api.get(`/articles?category=${encodeURIComponent(team.category)}`).then((r) => setArticles(r.data)).catch(() => {});
  }, [slug, team]);

  if (!team) return <Navigate to="/equipes" replace />;

  return (
    <>
      <SEO title={team.name} description={`${team.name} du Club Cycliste Le Boulou : présentation, effectif, calendrier, résultats et actualités.`} />
      <PageHeader kicker={team.kicker} title={team.name} image={team.image} position={team.position} />

      <section className="py-16 sm:py-20 border-b border-navy/10" data-testid="team-presentation">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_360px] gap-10">
          <Reveal>
            <h2 className="font-heading uppercase font-semibold tracking-tight text-rouge text-2xl sm:text-3xl mb-8">
              {team.presentation.tagline}
            </h2>
            <div className="space-y-5 text-navy/80 text-base sm:text-lg leading-relaxed max-w-3xl">
              {team.presentation.paragraphs.map((p, i) =>
                typeof p === "string" ? (
                  <p key={i}>{p}</p>
                ) : (
                  <div key={i}>
                    <h3 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl mb-2">{p.h}</h3>
                    <p>{p.t}</p>
                  </div>
                )
              )}
            </div>
            <div className="mt-12 bg-navy text-white p-8 sm:p-10 max-w-3xl" data-testid="team-callout">
              <h3 className="font-heading uppercase font-semibold tracking-tight text-2xl sm:text-3xl">
                {team.presentation.callout.title}
              </h3>
              <div className="mt-5 space-y-4 text-white/75 leading-relaxed">
                {team.presentation.callout.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid="team-callout-cta"
                className="mt-8 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
              >
                Nous contacter
              </Link>
            </div>
          </Reveal>
          <div>
            <Reveal delay={0.15}>
              <div className="bg-navy text-white p-7">
                <p className="font-heading uppercase tracking-wider text-sm text-rouge mb-4">Suivre l'équipe</p>
                <ul className="space-y-2.5 text-sm text-white/70">
                  <li><a href="#effectif" className="hover:text-rouge transition-colors">— Effectif</a></li>
                  <li><a href="#calendrier-equipe" className="hover:text-rouge transition-colors">— Calendrier</a></li>
                  <li><a href="#resultats-equipe" className="hover:text-rouge transition-colors">— Résultats</a></li>
                  <li><a href="#actus-equipe" className="hover:text-rouge transition-colors">— Actualités</a></li>
                </ul>
              </div>
            </Reveal>
            {team.videoSuivre && (
              <Reveal delay={0.2}>
                <div className="mt-6 overflow-hidden bg-navy" data-testid="team-video-suivre">
                  <video autoPlay muted loop playsInline className="w-full object-cover">
                    <source src={team.videoSuivre} type="video/mp4" />
                    <source src={team.videoSuivre.replace(/\.mp4$/i, ".webm")} type="video/webm" />
                  </video>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <div id="effectif" className="scroll-mt-28">
        <Block title="Effectif" testId="team-roster">
          <EmptyState
            title="Effectif bientôt publié"
            message="La liste des coureurs et coureuses sera publiée ici. Elle sera gérée directement par le club depuis l'administration."
            testId="team-roster-empty"
          />
        </Block>
      </div>

      <Block title="Encadrement" testId="team-staff">
        {team.staff?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-staff-list">
            {team.staff.map((s) => (
              <div key={s.role} className="border border-navy/10 bg-white px-6 py-5 hover:border-rouge transition-colors duration-300">
                <p className="font-heading uppercase tracking-wider text-[11px] text-steel">{s.role}</p>
                <p className="mt-1.5 font-heading uppercase font-semibold text-navy text-lg leading-snug">{s.names}</p>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Encadrement bientôt présenté"
            message="Les encadrants de l'équipe seront présentés ici prochainement."
            testId="team-staff-empty"
          />
        )}
      </Block>

      <div id="calendrier-equipe" className="scroll-mt-28">
        <Block title="Calendrier" testId="team-calendar">
          {events.length === 0 ? (
            <EmptyState title="Calendrier à venir" message="Les compétitions et événements de l'équipe seront publiés ici." testId="team-calendar-empty" />
          ) : (
            <div className="border-t border-navy/10">
              {events.map((e) => {
                const d = dayMonth(e.date);
                return (
                  <div key={e.id} className="flex items-center gap-5 border-b border-navy/10 py-5">
                    <div className="shrink-0 w-[64px] bg-navy text-white text-center py-2.5">
                      <span className="block font-heading font-semibold text-xl leading-none">{d.day}</span>
                      <span className="block text-[10px] uppercase tracking-widest mt-1 opacity-80">{d.month}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading uppercase font-semibold text-navy text-lg">{e.name}</p>
                      <p className="text-sm text-steel flex items-center gap-1.5"><MapPin size={13} className="text-rouge" />{e.location}</p>
                    </div>
                    <span className="shrink-0 uppercase tracking-wider text-[11px] font-semibold text-navy/60 border border-navy/15 px-2.5 py-1" data-testid={`team-event-cat-${e.id.slice(0, 8)}`}>
                      {e.categories || e.category}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Block>
      </div>

      <div id="resultats-equipe" className="scroll-mt-28">
        <Block title="Résultats" testId="team-results">
          {results.length === 0 ? (
            <EmptyState title="Résultats à venir" message="Les résultats de l'équipe seront publiés ici au fil de la saison." testId="team-results-empty" />
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {results.map((r) => (
                <div key={r.id} className="bg-white border border-navy/10 p-6">
                  <span className="font-heading font-semibold text-4xl text-rouge">{r.position}</span>
                  <p className="mt-3 font-heading uppercase font-semibold text-navy text-lg">{r.rider}</p>
                  <p className="text-sm text-steel">{r.competition} — {fmtDate(r.date)}</p>
                </div>
              ))}
            </div>
          )}
        </Block>
      </div>

      <div id="actus-equipe" className="scroll-mt-28">
        <Block title="Actualités de l'équipe" testId="team-news">
          {articles.length === 0 ? (
            <EmptyState title="Aucune actualité pour le moment" message="Les articles liés à l'équipe apparaîtront ici." testId="team-news-empty" />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, i) => (
                <NewsCard key={a.id} article={a} index={i} />
              ))}
            </div>
          )}
        </Block>
      </div>

      <section className="bg-navy py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-6">
          <p className="font-heading uppercase font-semibold tracking-tight text-white text-2xl sm:text-3xl">
            Découvrir les autres équipes
          </p>
          <Link
            to="/equipes"
            className="inline-flex items-center gap-3 border border-white/30 text-white font-heading uppercase tracking-wider text-sm px-6 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
          >
            Toutes les équipes
          </Link>
        </div>
      </section>
    </>
  );
}
